<template>
  <div ref="homePageContent" class="hpc-Content">
    <PageContentHeader :max-width="HEADER_SIZES.FULL_NEXT" class="hpc-Content_Header" no-margin>
      <template #header-main-content>
        <div class="hpc-HeaderTitle oboard-truncated-text">
          {{ $t('homepage.greetings_username', { userName }) }} {{ greetingsEmoji }}
        </div>
      </template>
      <div class="hpc-HeaderActions">
        <AppButtonNext
          v-if="isFullscreenSupported"
          v-tippy="{
            content: t('common.fullscreen')
          }"
          :data-auto-testid="HOMEPAGE_FULLSCREEN_BUTTON_TEST_ID"
          :data-testid="HOMEPAGE_FULLSCREEN_BUTTON_TEST_ID"
          :icon-before="isFullscreen ? 'fullscreen-off' : 'fullscreen-on'"
          :variant="BUTTON_VARIANTS.TERTIARY"
          class="hpc-HeaderActions_Button"
          height="24"
          width="24"
          @click="toggleFullscreen"
        />
        <template v-if="areFiltersValuesResolved && selectedUserOrGroup">
          <AppButtonNext
            v-if="canExploreOtherPeopleProgress"
            v-tippy="{
              content: isEntityPinned ? t('action.unpin') : t('action.pin')
            }"
            :data-auto-testid="HOMEPAGE_PIN_BUTTON_TEST_ID"
            :data-testid="HOMEPAGE_PIN_BUTTON_TEST_ID"
            :disabled="isPinLoading"
            :icon-before="isEntityPinned ? 'unpin' : 'pin'"
            :variant="BUTTON_VARIANTS.TERTIARY"
            class="hpc-HeaderActions_Button"
            height="24"
            width="24"
            @click="togglePin"
          />

          <UseAppPlatform v-slot="{ isCrossPlatformApp }">
            <AppButtonNext
              v-if="!isCrossPlatformApp"
              v-tippy="{
                content: t('action.share')
              }"
              :data-auto-testid="HOMEPAGE_SHARE_BUTTON_TEST_ID"
              :data-testid="HOMEPAGE_SHARE_BUTTON_TEST_ID"
              :loading="shareLinkLoading"
              :variant="BUTTON_VARIANTS.TERTIARY"
              class="hpc-HeaderActions_Button"
              height="24"
              icon-before="share"
              width="24"
              @click="onCopyClick"
            />
          </UseAppPlatform>

          <PersonalStatisticsOkrElementsModals
            ref="homePageOkrElementsActions"
            :selected-user-or-group="selectedUserOrGroup"
            :selected-workspace-ids="homePageFilters[NEW_HP_FILTERS_KEYS.WORKSPACE_IDS]"
            :skip-open-element-modal-from-query="
              homePageCurrentTab !== HOMEPAGE_TABS.OBJECTIVES.value
            "
            :user-selected="userSelected"
            @update-okr-elements="updateOkrElements"
          />
        </template>
      </div>
    </PageContentHeader>
    <div v-if="!isNil(intervalTypeId)" class="hpc-Content_Body">
      <HomePageFilters />
      <HomePageAiReports
        v-if="areFiltersValuesResolved"
        v-slot="{
          alignmentSection,
          generateAlignment,
          isAlignmentAvailable,
          isAlignmentGenerating,
          isAlignmentNothingToAnalyse,
          isLoading
        }"
        :grade-types="selectedGradeTypes"
        :interval-type-id="intervalTypeId"
        :selected-interval-ids="selectedIntervalIds"
        :selected-user-or-group="selectedUserOrGroup"
        :user-selected="userSelected"
        :workspace-ids="homePageFilters[NEW_HP_FILTERS_KEYS.WORKSPACE_IDS]"
      >
        <HomePageAverageProgress
          ref="homePageAvgProgress"
          :alignment-section="alignmentSection"
          :data-auto-testid="HOMEPAGE_AVERAGE_PROGRESS_TEST_ID"
          :data-testid="HOMEPAGE_AVERAGE_PROGRESS_TEST_ID"
          :is-alignment-available="isAlignmentAvailable"
          :is-alignment-generating="isAlignmentGenerating"
          :is-alignment-loading="isLoading"
          :is-alignment-nothing-to-analyse="isAlignmentNothingToAnalyse"
          @generate-alignment="generateAlignment"
        />
      </HomePageAiReports>
      <div
        :data-auto-testid="HOMEPAGE_TABS_TEST_ID"
        :data-testid="HOMEPAGE_TABS_TEST_ID"
        class="hpc-BodyItems"
      >
        <AppTabs v-model="homePageCurrentTab" :options="homePageTabs">
          <template #[objectivesTabName]>
            <div class="hpc-BodyItems_Tab">
              <AppMultipleRadioGroup
                v-model="selectedGradeTypes"
                :data-auto-testid="HOMEPAGE_STATUS_FILTER_TEST_ID"
                :data-testid="HOMEPAGE_STATUS_FILTER_TEST_ID"
                :given-values-on-reset="DEFAULT_GRADE_TYPES_ON_RESET"
                :options="gradeTypeOptions"
                class="hpc-BodyItems_Tab_GradeTypes"
                name="hpGradeTypes"
                reset-on-last-unselect
              >
                <template #before-label="{ item }">
                  <span
                    :style="{
                      backgroundColor: item.color
                    }"
                    class="hpc-BodyItems_Tab_Dot"
                  />
                </template>
              </AppMultipleRadioGroup>
              <div class="hpc-BodyItems_Cards">
                <HomePageCard
                  v-if="areFiltersValuesResolved && showOwnerCards"
                  ref="homePageObjectivesCard"
                  v-model:open="objectivesOpen"
                  :data-auto-testid="HOMEPAGE_OBJECTIVES_CARD_TEST_ID"
                  :data-testid="HOMEPAGE_OBJECTIVES_CARD_TEST_ID"
                  :list-type="USER_LIST_TYPES.OWNERS"
                  :search-type-id="SEARCH_TYPES.HOMEPAGE_OBJECTIVES"
                  :selected-dates="selectedDates"
                  :selected-intervals="selectedIntervalIds"
                  :selected-user-or-group="selectedUserOrGroup"
                  :user-selected="userSelected"
                  has-elements-progress
                  icon="menu-objectives"
                  show-last-checkin
                  @edit-element="editElement"
                  @link-jira-issue="linkJiraIssue"
                  @create-jira-issue="createJiraIssue"
                  @link-work-item="linkWorkItem"
                  @create-work-item="createWorkItem"
                  @create-key-result="createKeyResult"
                  @update-okr-elements="updateOkrElements"
                  @open-checkin-modal="openCheckinModal"
                >
                  <template #accordion:title>
                    {{ $t('dashboard.objectives') }}
                  </template>
                  <template #header>
                    <CardObjectivesHeader />
                  </template>
                  <template #loader>
                    <CardObjectivesLoader />
                  </template>
                </HomePageCard>
                <HomePageCard
                  v-if="areFiltersValuesResolved && showOwnerCards"
                  ref="homePageMetricsCard"
                  v-model:open="metricsOpen"
                  :data-auto-testid="HOMEPAGE_KEY_RESULTS_CARD_TEST_ID"
                  :data-testid="HOMEPAGE_KEY_RESULTS_CARD_TEST_ID"
                  :list-type="USER_LIST_TYPES.METRICS_WITHOUT_KPI"
                  :search-type-id="SEARCH_TYPES.METRICS_WITHOUT_KPI"
                  :selected-dates="selectedDates"
                  :selected-intervals="selectedIntervalIds"
                  :selected-user-or-group="selectedUserOrGroup"
                  :user-selected="userSelected"
                  has-elements-progress
                  icon="metric-chart"
                  show-last-checkin
                  @edit-element="editElement"
                  @link-jira-issue="linkJiraIssue"
                  @create-jira-issue="createJiraIssue"
                  @link-work-item="linkWorkItem"
                  @create-work-item="createWorkItem"
                  @create-key-result="createKeyResult"
                  @update-okr-elements="updateOkrElements"
                  @open-checkin-modal="openCheckinModal"
                >
                  <template #accordion:title>
                    {{ $t('objectives.krs') }}
                  </template>
                  <template #header="{ withTopBorder }">
                    <CardObjectivesHeader :with-top-border="withTopBorder">
                      <template #cell:item-name>
                        {{ t('homepage.key_result_qty', 0) }}
                      </template>
                    </CardObjectivesHeader>
                  </template>
                  <template #loader>
                    <CardObjectivesLoader />
                  </template>
                </HomePageCard>
                <HomePageCard
                  v-if="areFiltersValuesResolved && userSelected"
                  ref="homePageStakeholdersCard"
                  v-model:open="stakeholdersOpen"
                  :data-auto-testid="HOMEPAGE_STAKEHOLDERS_CARD_TEST_ID"
                  :data-testid="HOMEPAGE_STAKEHOLDERS_CARD_TEST_ID"
                  :list-type="USER_LIST_TYPES.STAKEHOLDERS"
                  :search-type-id="SEARCH_TYPES.HOMEPAGE_OBJECTIVES"
                  :selected-dates="selectedDates"
                  :selected-intervals="selectedIntervalIds"
                  :selected-user-or-group="selectedUserOrGroup"
                  :user-selected="userSelected"
                  has-elements-progress
                  icon="users-outline-next"
                  show-last-checkin
                  @edit-element="editElement"
                  @link-jira-issue="linkJiraIssue"
                  @create-jira-issue="createJiraIssue"
                  @link-work-item="linkWorkItem"
                  @create-work-item="createWorkItem"
                  @create-key-result="createKeyResult"
                  @update-okr-elements="updateOkrElements"
                  @open-checkin-modal="openCheckinModal"
                >
                  <template #accordion:title>
                    {{ $t('homepage.stakeholder.title') }}
                  </template>
                  <template #header>
                    <CardObjectivesHeader />
                  </template>
                  <template #loader>
                    <CardObjectivesLoader />
                  </template>
                </HomePageCard>
              </div>
            </div>
          </template>
          <template #[checkInsTabName]>
            <CheckinsFeedLayout
              v-if="areFiltersValuesResolved"
              ref="checkinsFeedLayout"
              :adjust-scroll-top="adjustHomePageFeedScrollTop"
              :data-auto-testid="HOMEPAGE_CHECKINS_FEED_TEST_ID"
              :data-testid="HOMEPAGE_CHECKINS_FEED_TEST_ID"
              :external-filters="externalFilters"
              :get-scroll-top="getHomePageFeedScrollTop"
              hide-banner
              hide-filters
              @elements-updated="onElementsUpdated"
            />
          </template>
          <template #[dashboardTabName]>
            <HomePageDashboard
              v-if="areFiltersValuesResolved"
              ref="homePageDashboard"
              :data-auto-testid="HOMEPAGE_DASHBOARD_TEST_ID"
              :data-testid="HOMEPAGE_DASHBOARD_TEST_ID"
              :drag-area="homePageContentRef"
              @elements-updated="onElementsUpdated"
            />
          </template>
        </AppTabs>
      </div>
    </div>

    <UseAppPlatform v-slot="{ isJiraApp, isAzureApp, isCrossPlatformApp }">
      <CreateJiraIssueForm
        v-if="isJiraApp"
        ref="createJiraIssueForm"
        source="form"
        @save="onCreateJiraIssueFormSave"
      />

      <CreateWorkItemForm
        v-if="isAzureApp"
        ref="createWorkItemForm"
        source="form"
        @save="onCreateWorkItemFormSave"
      />

      <CopySharedLinkModal
        v-if="!isCrossPlatformApp"
        v-model:show="showCopyShareLinkModal"
        :token="shareToken"
      />
    </UseAppPlatform>
  </div>
</template>

<script setup>
import { isEqual, isNil, isNull } from 'lodash'
import { computed, inject, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { SEARCH_TYPES } from '@/api/okr-elements'
import { useOboardFullscreen } from '@/composables/fullscreen'
import { useWorkspacesLevels } from '@/composables/workspaces-levels'
import { getTabSlotName } from '@/utils/app-tabs'
import { useCreateWorkItem } from '@/utils/azure-devops-helpers/work-item'
import { BUTTON_VARIANTS } from '@/utils/components-configurations/app-button'
import { HEADER_SIZES } from '@/utils/components-configurations/page-content-header'
import {
  DEFAULT_GRADE_TYPES_ON_RESET,
  HOMEPAGE_GRADE_TYPES_OPTIONS,
  HOMEPAGE_OBJECTIVES_CARD_STATE_KEYS,
  HOMEPAGE_TABS,
  useHomepageFiltersPersistence,
  useHomepageProgressExploring,
  USER_LIST_TYPES
} from '@/utils/homepage'
import { homePageStateInjectionKey } from '@/utils/injection-keys'
import { OBJECTIVE_TYPES } from '@/utils/objective-types'
import { OKR_FORM_VIEWS } from '@/utils/objectives'
import { NEW_HP_FILTERS_KEYS } from '@/utils/okr-elements/filters'
import { useElementOwner } from '@/utils/okr-elements-forms-helpers'
import { useLoadingState } from '@/utils/use-loading-state'
import {
  DEFAULT_USERS_SETTINGS,
  HOMEPAGE_ACTIVE_TAB,
  HOMEPAGE_OBJECTIVES_CARD_STATE,
  USER_SETTINGS_MAPPER
} from '@/utils/user-settings'
import { getFirstName } from '@/utils/users-helpers'

import CardObjectivesHeader from '@/components/home-page/CardObjectivesHeader'
import CardObjectivesLoader from '@/components/home-page/CardObjectivesLoader'
import HomePageAiReports from '@/components/home-page/HomePageAiReports'
import HomePageAverageProgress from '@/components/home-page/HomePageAverageProgress'
import HomePageCard from '@/components/home-page/HomePageCard'
import HomePageDashboard from '@/components/home-page/HomePageDashboard'
import HomePageFilters from '@/components/home-page/HomePageFilters'
import {
  HOMEPAGE_AVERAGE_PROGRESS_TEST_ID,
  HOMEPAGE_CHECKINS_FEED_TEST_ID,
  HOMEPAGE_DASHBOARD_TEST_ID,
  HOMEPAGE_FULLSCREEN_BUTTON_TEST_ID,
  HOMEPAGE_KEY_RESULTS_CARD_TEST_ID,
  HOMEPAGE_OBJECTIVES_CARD_TEST_ID,
  HOMEPAGE_PIN_BUTTON_TEST_ID,
  HOMEPAGE_SHARE_BUTTON_TEST_ID,
  HOMEPAGE_STAKEHOLDERS_CARD_TEST_ID,
  HOMEPAGE_STATUS_FILTER_TEST_ID,
  HOMEPAGE_TABS_TEST_ID
} from '@/components/home-page/jest-helpers'
import PersonalStatisticsOkrElementsModals from '@/components/personal-statistics/PersonalStatisticsOkrElementsModals'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppMultipleRadioGroup from '@/components/ui/AppMultipleRadioGroup/AppMultipleRadioGroup'
import AppTabs from '@/components/ui/AppTabs/AppTabs'
import { useCopySharedLinkModal } from '@/components/ui/CopySharedLinkModal/composables'
import CopySharedLinkModal from '@/components/ui/CopySharedLinkModal/CopySharedLinkModal'
import PageContentHeader from '@/components/ui/PageContentHeader/PageContentHeader'
import UseAppPlatform from '@/components/UseAppPlatform'
import CheckinsFeedLayout from '@/views/check-ins/CheckinsFeedLayout'
import CreateJiraIssueForm from '@jira/components/objectives/forms/CreateJiraIssue'
import CreateWorkItemForm from '@jira/components/objectives/forms/CreateWorkItemForm'

defineOptions({
  name: 'HomePageContent'
})

const store = useStore()
const { t } = useI18n()
const { isFullscreen, isFullscreenSupported, toggleFullscreen } = useOboardFullscreen({
  exitOnUnmount: true
})
const userName = computed(() => getFirstName(store.state.system.userData?.userName || ''))
const greetingsEmoji = '👋'

const { isElementOwnerFieldHidden } = useElementOwner({ typeId: OBJECTIVE_TYPES.PERSONAL })

const showOwnerCards = computed(() => {
  if (!userSelected.value) {
    return true
  }

  return !isElementOwnerFieldHidden.value
})

const checkinFeedForUsersVisibility = computed(
  () => store.getters['system/checkinFeedForUsersVisibility']
)

const dashboardsVisibility = computed(() => store.getters['system/dashboardsVisibility'])

const homepageObjectivesCardState = computed(
  () => store.getters['system/homepageObjectivesCardState']
)

const getCardOpenState = key =>
  homepageObjectivesCardState.value[key] ??
  DEFAULT_USERS_SETTINGS[HOMEPAGE_OBJECTIVES_CARD_STATE][key]

const persistCardOpenState = (key, isOpen) => {
  store.dispatch('system/updateUserSettings', {
    [USER_SETTINGS_MAPPER[HOMEPAGE_OBJECTIVES_CARD_STATE]]: {
      ...homepageObjectivesCardState.value,
      [key]: isOpen
    }
  })
}

const objectivesOpen = ref(getCardOpenState(HOMEPAGE_OBJECTIVES_CARD_STATE_KEYS.OBJECTIVES))
const metricsOpen = ref(getCardOpenState(HOMEPAGE_OBJECTIVES_CARD_STATE_KEYS.METRICS))
const stakeholdersOpen = ref(getCardOpenState(HOMEPAGE_OBJECTIVES_CARD_STATE_KEYS.STAKEHOLDERS))

watch(objectivesOpen, isOpen =>
  persistCardOpenState(HOMEPAGE_OBJECTIVES_CARD_STATE_KEYS.OBJECTIVES, isOpen)
)
watch(metricsOpen, isOpen =>
  persistCardOpenState(HOMEPAGE_OBJECTIVES_CARD_STATE_KEYS.METRICS, isOpen)
)
watch(stakeholdersOpen, isOpen =>
  persistCardOpenState(HOMEPAGE_OBJECTIVES_CARD_STATE_KEYS.STAKEHOLDERS, isOpen)
)

const { canExploreOtherPeopleProgress } = useHomepageProgressExploring()

const homePageTabs = computed(() =>
  Object.values(HOMEPAGE_TABS)
    .filter(tab => {
      if (tab.value === HOMEPAGE_TABS.CHECK_INS.value) {
        return checkinFeedForUsersVisibility.value
      }

      if (tab.value === HOMEPAGE_TABS.DASHBOARD.value) {
        return (
          dashboardsVisibility.value.showCustomDashboardForUsers &&
          canExploreOtherPeopleProgress.value
        )
      }

      return true
    })
    .map(tab => ({
      ...tab,
      label: t(tab.label)
    }))
)

const savedActiveTab = store.getters['system/homepageActiveTab']

const homePageCurrentTab = ref(
  homePageTabs.value.some(tab => tab.value === savedActiveTab)
    ? savedActiveTab
    : homePageTabs.value[0].value
)

const saveActiveTab = activeTab => {
  store.dispatch('system/updateUserSettings', {
    [USER_SETTINGS_MAPPER[HOMEPAGE_ACTIVE_TAB]]: activeTab
  })
}

if (homePageCurrentTab.value !== savedActiveTab) {
  saveActiveTab(homePageCurrentTab.value)
}

watch(homePageCurrentTab, activeTab => {
  saveActiveTab(activeTab)
})

const objectivesTabName = getTabSlotName(HOMEPAGE_TABS.OBJECTIVES.value)
const checkInsTabName = getTabSlotName(HOMEPAGE_TABS.CHECK_INS.value)
const dashboardTabName = getTabSlotName(HOMEPAGE_TABS.DASHBOARD.value)

const checkinsFeedLayoutRef = useTemplateRef('checkinsFeedLayout')
const homePageContentRef = useTemplateRef('homePageContent')

/** @returns {number} */
const getHomePageFeedScrollTop = () => {
  const container = homePageContentRef.value
  const layout = checkinsFeedLayoutRef.value?.getFeedLayoutContainer?.()
  if (!container || !layout) return 0

  return container.getBoundingClientRect().top - layout.getBoundingClientRect().top
}

const adjustHomePageFeedScrollTop = offset => {
  const container = homePageContentRef.value
  if (!container) return

  container.scrollTop = Math.max(0, container.scrollTop - offset)
}

const { firstWorkItemKindId } = useCreateWorkItem()

const homePageObjectivesCard = useTemplateRef('homePageObjectivesCard')
const homePageMetricsCard = useTemplateRef('homePageMetricsCard')
const homePageStakeholdersCard = useTemplateRef('homePageStakeholdersCard')
const homePageOkrElementsActions = useTemplateRef('homePageOkrElementsActions')
const homePageAvgProgress = useTemplateRef('homePageAvgProgress')
const homePageDashboard = useTemplateRef('homePageDashboard')
const createJiraIssueForm = useTemplateRef('createJiraIssueForm')
const createWorkItemForm = useTemplateRef('createWorkItemForm')

const updateOkrElements = ({ loadEntityForAverageProgress, reloadDashboard = true } = {}) => {
  homePageObjectivesCard.value?.updateOkrElements()
  homePageMetricsCard.value?.updateOkrElements()
  homePageStakeholdersCard.value?.updateOkrElements()

  updateAverageProgress({
    loadEntity: loadEntityForAverageProgress
  })

  if (reloadDashboard) {
    homePageDashboard.value?.reloadDashboard()
  }
}

const updateAverageProgress = params => {
  homePageAvgProgress.value?.loadAvgProgress(params)
}

const homePageState = inject(homePageStateInjectionKey)
const homePageFilters = homePageState.filterValues
const { persistFilters } = useHomepageFiltersPersistence(homePageFilters)
const areFiltersValuesResolved = homePageState.areFiltersValuesResolved
const selectedIntervalIds = homePageState.selectedIntervalIds
const intervalTypeId = computed(() => {
  return homePageFilters.value[NEW_HP_FILTERS_KEYS.INTERVAL_TYPE_ID]
})

const selectedDates = computed(() => {
  return {
    startDates: homePageFilters.value[NEW_HP_FILTERS_KEYS.START_DATES],
    dueDates: homePageFilters.value[NEW_HP_FILTERS_KEYS.DUE_DATES]
  }
})

const hasValueChanged = ({ newValue, oldValue, keys, key } = {}) => {
  const index = keys.indexOf(key)

  if (index === -1) return false

  return !isEqual(newValue[index], oldValue[index])
}

// NOTE: why Object.values? for watch(homePageFilters),
// newValue and oldValue are the same object, because
// only properties are mutated in homePageFilters, not
// the object itself
watch(
  () => Object.values(homePageFilters.value),
  (newValue, oldValue) => {
    if (!areFiltersValuesResolved.value) {
      return
    }

    if (isEqual(newValue, oldValue)) {
      return
    }

    const userChanged = hasValueChanged({
      newValue,
      oldValue,
      keys: Object.keys(homePageFilters.value),
      key: NEW_HP_FILTERS_KEYS.SELECTED_USER
    })
    const groupChanged = hasValueChanged({
      newValue,
      oldValue,
      keys: Object.keys(homePageFilters.value),
      key: NEW_HP_FILTERS_KEYS.SELECTED_GROUP
    })

    persistFilters()
    // CustomDashboardItem reloads itself from its groupId prop watcher and its request does not
    // depend on the other Home filters, so a reload here would request the previous group id
    updateOkrElements({
      loadEntityForAverageProgress: userChanged || groupChanged,
      reloadDashboard: false
    })
  },
  { deep: true }
)

useWorkspacesLevels(() => homePageFilters.value[NEW_HP_FILTERS_KEYS.WORKSPACE_IDS])

const externalFilters = computed(() => {
  const selectedUserId = homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_USER]
  const selectedGroupId = homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_GROUP]

  return {
    assigneeIds: isNull(selectedUserId) ? null : [selectedUserId],
    groupIds: isNull(selectedGroupId) ? null : [selectedGroupId]
  }
})

const selectedUserOrGroup = computed(() => {
  return (
    homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_USER] ||
    homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_GROUP] ||
    null
  )
})

const userSelected = computed(() => {
  return !isNull(homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_USER])
})

const groupSelected = computed(() => {
  return !isNull(homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_GROUP])
})

//#region element actions
const editElement = ({ view, item, scrollToComments }) => {
  homePageOkrElementsActions.value?.editObjective(view, item, { scrollToComments })
}

const onElementsUpdated = () => {
  updateAverageProgress({ loadEntity: false })
}

const openCheckinModal = payload => {
  homePageOkrElementsActions.value?.openCheckinModal(payload)
}

const linkJiraIssue = ({ view, data }) => {
  homePageOkrElementsActions.value?.linkJiraIssue(view, data)
}

const createJiraIssue = objective => {
  createJiraIssueForm.value?.create(objective)
}

const linkWorkItem = ({ view, data }) => {
  homePageOkrElementsActions.value?.linkWorkItem(view, data)
}

const createWorkItem = objective => {
  if (firstWorkItemKindId.value && createWorkItemForm.value) {
    createWorkItemForm.value.create(objective, {
      workItemKindId: firstWorkItemKindId.value
    })
  }
}

const createKeyResult = objective => {
  homePageOkrElementsActions.value?.createKR(OKR_FORM_VIEWS.KR, objective)
}

const onCreateJiraIssueFormSave = () => {
  updateOkrElements()
}

const onCreateWorkItemFormSave = () => {
  updateOkrElements()
}
//#endregion

//#region status filter
const selectedGradeTypes = computed({
  get() {
    return homePageFilters.value[NEW_HP_FILTERS_KEYS.GRADE_TYPES]
  },
  set(value) {
    homePageFilters.value[NEW_HP_FILTERS_KEYS.GRADE_TYPES] = value
  }
})
const gradeTypeOptions = computed(() => {
  return HOMEPAGE_GRADE_TYPES_OPTIONS.map(e => ({
    ...e,
    label: t(e.label)
  }))
})
//#endregion

const isEntityPinned = computed(() => {
  if (userSelected.value) {
    return store.getters['system/isUserPinned']({
      accountId: homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_USER]
    })
  }

  if (groupSelected.value) {
    return store.getters['system/isGroupPinned']({
      groupId: homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_GROUP]
    })
  }

  return false
})

const { isLoading: isPinLoading, toggleLoading: togglePinLoading } = useLoadingState()

const togglePin = async () => {
  togglePinLoading(true)

  if (userSelected.value) {
    await store.dispatch('system/togglePinStatusForUser', {
      accountId: homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_USER]
    })
  }

  if (groupSelected.value) {
    await store.dispatch('system/togglePinStatusForGroup', {
      groupId: homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_GROUP]
    })
  }

  togglePinLoading(false)
}

const { onCopyClick, shareToken, shareLinkLoading, showCopyShareLinkModal } =
  useCopySharedLinkModal()
</script>

<style lang="scss" scoped>
.hpc-Content {
  height: 100%;
  // background-color: var(--gray-blue-0);
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
  overflow: auto;
  width: 100%;
}

.hpc-HeaderActions {
  display: flex;
  gap: 16px;
  align-items: center;
}

.hpc-HeaderActions_Button {
  --abn-color: #{$dark-2};
}

.hpc-Content_Header {
  --padding-bottom: 0px;
  --ob-pch-background-color: transparent;
  min-width: $page-width-md-next;
  width: 100%;
  max-width: 1128px;
  margin: 0 auto;
}

.hpc-Content_Body {
  padding: 0 $page-right-padding 32px $page-left-padding;
  width: $page-width-md-next;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;

  --ob-cdmi-margin: 0 0 0 -#{$page-left-padding};
  --ob-dil-margin: 0 0 0 -#{$page-left-padding};
}

.hpc-HeaderTitle {
  color: var(--gray-900);

  font-size: $fs-30;
  font-weight: fw('bold');
  line-height: 38px;
  white-space: nowrap;
}

$body-items-gap: 16px;

.hpc-BodyItems {
  display: flex;
  flex-direction: column;

  --ob-at-margin-bottom: 0;

  // check-ins feed layout
  --ob-cfl-overflow-y: visible;
  --ob-cfl-height: auto;
  --ob-cfl-background: transparent;
  --ob-cft-padding-top: #{$body-items-gap};
  --ob-cfl-toolbar-fade: #{$white};

  --ob-dad-padding: 32px;
}

.hpc-BodyItems_Tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: $body-items-gap;
  --ob-oeli-interval-width: 100px;
  --ob-oeli-grade-cell-min-width: 91px;
  --grade-header-cell-width: 135px;
}

.hpc-BodyItems_Cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.hpc-BodyItems_Tab_GradeTypes {
  --ob-amrg-Option-padding: 4px 8px;
  --ob-amrg-Option-gap: 8px;
}

.hpc-BodyItems_Tab_Dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>
